/**
 * Image Caching Service
 * Handles client-side image caching and preloading
 */

class ImageCacheService {
  private cache = new Map<string, string>();
  private preloadQueue: string[] = [];
  private isPreloading = false;

  /**
   * Get cached image or load and cache it
   */
  async getCachedImage(imageUrl: string): Promise<string> {
    if (!imageUrl) return '';

    // For Azure Blob Storage URLs, just return the original URL
    // The browser will handle caching via the Cache-Control headers
    if (imageUrl.includes('blob.core.windows.net')) {
      return imageUrl;
    }

    // Return cached version if available
    if (this.cache.has(imageUrl)) {
      return this.cache.get(imageUrl)!;
    }

    // Load and cache the image
    try {
      const cachedUrl = await this.loadAndCacheImage(imageUrl);
      return cachedUrl;
    } catch (error) {
      console.warn('Failed to cache image:', imageUrl, error);
      return imageUrl; // Fallback to original URL
    }
  }

  /**
   * Load image and create a cached blob URL
   */
  private async loadAndCacheImage(imageUrl: string): Promise<string> {
    try {
      // For Azure Blob Storage URLs, we need to handle CORS differently
      const isAzureBlob = imageUrl.includes('blob.core.windows.net');
      
      const response = await fetch(imageUrl, {
        mode: isAzureBlob ? 'no-cors' : 'cors',
        credentials: 'include',
        cache: 'force-cache' // Use browser cache when available
      });
      
      if (!response.ok && response.type !== 'opaque') {
        throw new Error(`Failed to load image: ${response.status}`);
      }

      const blob = await response.blob();
      const cachedUrl = URL.createObjectURL(blob);
      
      // Store in cache
      this.cache.set(imageUrl, cachedUrl);
      
      return cachedUrl;
    } catch (error) {
      console.warn('Failed to cache image, using original URL:', imageUrl, error);
      // Return original URL as fallback
      return imageUrl;
    }
  }

  /**
   * Preload multiple images in the background
   */
  async preloadImages(imageUrls: string[]): Promise<void> {
    if (this.isPreloading) return;
    
    // Filter out Azure Blob Storage URLs as they don't need preloading
    const filteredUrls = imageUrls.filter(url => 
      url && !url.includes('blob.core.windows.net')
    );
    
    if (filteredUrls.length === 0) return;
    
    this.isPreloading = true;
    this.preloadQueue = [...filteredUrls];

    try {
      // Process images in batches to avoid overwhelming the browser
      const batchSize = 5;
      for (let i = 0; i < this.preloadQueue.length; i += batchSize) {
        const batch = this.preloadQueue.slice(i, i + batchSize);
        await Promise.allSettled(
          batch.map(url => this.getCachedImage(url))
        );
        
        // Small delay between batches
        if (i + batchSize < this.preloadQueue.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    } finally {
      this.isPreloading = false;
      this.preloadQueue = [];
    }
  }

  /**
   * Preload images for equipment list
   */
  async preloadEquipmentImages(equipmentList: any[]): Promise<void> {
    const imageUrls = equipmentList
      .map(item => item.image_url)
      .filter(url => url && !this.cache.has(url));
    
    if (imageUrls.length > 0) {
      await this.preloadImages(imageUrls);
    }
  }

  /**
   * Clear cache (useful for memory management)
   */
  clearCache(): void {
    // Revoke all blob URLs to free memory
    this.cache.forEach(url => {
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; urls: string[] } {
    return {
      size: this.cache.size,
      urls: Array.from(this.cache.keys())
    };
  }
}

const imageCacheService = new ImageCacheService();
export default imageCacheService;
